/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React, { useCallback, useEffect } from 'react';
import { makeStyles } from '@material-ui/core';
import {
    shape, string, func, bool,
    object,
} from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect, useDispatch } from 'react-redux';
import { compose } from 'recompose';
import { trackEvent } from '../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import SiteWideBannerUI from './SiteWideBannerUI';
import useUTMRules from '../../../../helpers/DynamicRules/hooks/useUTMRules';
import useUserType from './UseUserType';
import isAfterCutoff from '../../../../helpers/determineCutoffTime';
import LinkOrATag from '../../GraphqlCommonComponents/LinkOrATag/LinkOrATag';
import OfferGatewaySiteWideUI from './OfferGatewaySiteWideUI';
import useOfferGatewayClickHandler from '../../GraphqlCommonComponents/GenericDialog/OfferGateway/useOfferGatewayClickHandler';
import noop from '../../../../helpers/noop';
import {
    BANNER_IMPRESSION_EVENT,
    BANNER_INTERACTION_EVENT,
    buildSiteWideBannerEvent,
} from './siteWideBannerTracking';

const useStyles = makeStyles(() => ({
    link: {
        textDecoration: 'none',
    },
}));

const SiteWideBannerContainer = ({
    bannerData, track, trackClickThrough, fireClickThrough, isOfferGatewayEnabled, salesforceResponse,
}) => {
    const showUserType = bannerData?.show_to_user_types || [];
    const enableSiteWideBanner = useUserType(showUserType);
    const isLink = bannerData?.linking?.link?.href.length;
    const cutoffWindow = bannerData?.cutoff_window;
    const classes = useStyles();
    const link = bannerData?.linking?.link;
    const ga = bannerData?.linking;
    const dispatch = useDispatch();

    const cutoffHandlers = {
        show_before: () => !isAfterCutoff(),
        show_after: () => isAfterCutoff(),
        show_always: () => true,
    };

    const isOfferBoxEnabled = bannerData?.offer_inbox_campaign_id?.length > 0;
    useEffect(() => {
        if (salesforceResponse?.campaign) {
            const trackingCategory = bannerData?.linking?.tracking_event_category;
            const trackingAction = bannerData?.linking?.tracking_event_action;
            const trackingLabel = bannerData?.linking?.tracking_event_label;
            dispatch(trackEvent({
                eventName: 'feature_interaction',
                eventCategory: trackingCategory || '',
                eventAction: trackingAction || '',
                eventLabel: trackingLabel || '',
                feature_detail: isOfferBoxEnabled ? 'Variant' : 'Control',
                feature_category: 'Offer Inbox',
                feature_element: 'Banner Impression',
            }));
        }
    }, [bannerData]);

    const campaignPayload = salesforceResponse.campaign?.campaignResponses?.[0]?.payload || {};
    const isOfferUnlocked = campaignPayload.offerFlow === 'Offer Unlocked' || campaignPayload.offerFlow === 'Offer Unlocked - SMS';
    const customButtons = salesforceResponse?.content?.custom_button;
    const lockButtons = customButtons?.find((item) => item?.offer_status === 'lock');
    const unlockButtons = customButtons?.find((item) => item?.offer_status === 'unlock' || item?.offer_status === 'copy');

    if (isOfferUnlocked && (campaignPayload?.offerEndState === 'Collection Page' && unlockButtons?.cta_button?.link?.href && campaignPayload?.collectionURL)) {
        if (campaignPayload?.offerBannerCode) {
            unlockButtons.cta_button.link.href = `${campaignPayload.collectionURL}?r=${campaignPayload.offerBannerCode}`;
        } else {
            unlockButtons.cta_button.link.href = campaignPayload.collectionURL;
        }
    }

    const buttonToShow = isOfferUnlocked ? unlockButtons : lockButtons;

    const { handleSiteWideBannerClick } = useOfferGatewayClickHandler(salesforceResponse);

    const isCutOffEnable = (cutoffHandlers[cutoffWindow] || (() => true))();
    const utmPassed = useUTMRules(bannerData);
    const shouldShowSingleBanner = enableSiteWideBanner && isCutOffEnable;

    const buildBannerEvent = useCallback((eventName) => buildSiteWideBannerEvent({
        eventName,
        bannerGroup: ga?.tracking_event_category,
        bannerType: ga?.tracking_event_action,
        bannerItemName: ga?.tracking_event_detail || ga?.tracking_event_label,
        clickId: `sitewide-banner-${bannerData?.uid || 'cta'}`,
        clickText: link?.title,
        clickUrl: link?.href,
    }), [
        bannerData?.uid,
        ga?.tracking_event_action,
        ga?.tracking_event_category,
        ga?.tracking_event_detail,
        ga?.tracking_event_label,
        link?.href,
        link?.title,
    ]);

    useEffect(() => {
        if (utmPassed && shouldShowSingleBanner) {
            track(buildBannerEvent(BANNER_IMPRESSION_EVENT));
        }
    }, [buildBannerEvent, shouldShowSingleBanner, track, utmPassed]);

    if (!utmPassed) {
        return <></>;
    }

    const handleTracking = (cat, act, lab) => {
        track({
            eventName: 'feature_interaction',
            eventCategory: cat || '',
            eventAction: act || '',
            eventLabel: lab || '',
        });
        if (fireClickThrough || isOfferBoxEnabled) trackClickThrough();
    };

    const handleBannerInteraction = () => {
        track(buildBannerEvent(BANNER_INTERACTION_EVENT));
    };

    if (!isOfferBoxEnabled && isOfferGatewayEnabled) {
        return (
            <div data-testid="og-bannerv2">
                <LinkOrATag
                    className={classes.link}
                    href={buttonToShow?.cta_button?.link?.href || '#'}
                    title={buttonToShow?.cta_button?.link?.href}
                    handleCallback={() => handleSiteWideBannerClick(buttonToShow)}
                >
                    <OfferGatewaySiteWideUI bannerData={bannerData} clickHandler={handleTracking} salesforceResponse={salesforceResponse} />
                </LinkOrATag>
            </div>
        );
    }

    return (
        shouldShowSingleBanner
        && (
            <div data-testid="bannerv2">
                {isLink ? (
                    <LinkOrATag
                        className={classes.link}
                        href={link.href}
                        title={link.title}
                        ignoreImpression="yes"
                    >
                        <SiteWideBannerUI bannerData={bannerData} clickHandler={handleTracking} bannerInteractionHandler={handleBannerInteraction} />
                    </LinkOrATag>
                ) : (
                    <SiteWideBannerUI bannerData={bannerData} clickHandler={handleTracking} bannerInteractionHandler={handleBannerInteraction} />
                )}
            </div>
        )
    );
};

SiteWideBannerContainer.defaultProps = {
    bannerData: {},
    fireClickThrough: false,
    trackClickThrough: noop,
    isOfferGatewayEnabled: false,
    salesforceResponse: {},
};

SiteWideBannerContainer.propTypes = {
    bannerData: shape({
        about_the_banner_message_: string,
        message: string,
        title: string,
        styling: shape({
            background_color: shape({
                color: string,
            }),
            cta_font: string,
        }),
    }),
    track: func.isRequired,
    fireClickThrough: bool,
    trackClickThrough: func,
    isOfferGatewayEnabled: bool,
    salesforceResponse: object,
};
const mapDispatchToProps = (dispatch) => ({
    track: bindActionCreators(trackEvent, dispatch),
});
const enhance = compose(
    connect(null, mapDispatchToProps),
);
export default enhance(SiteWideBannerContainer);
